
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

const FAQ = () => {
  const { t } = useTranslation(['common']);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqs = [
    {
      category: "Erste Schritte",
      questions: [
        {
          question: "Was ist uplivion und wie funktioniert es?",
          answer: "uplivion ist eine Plattform zur Erstellung persönlicher Lebens-Timelines. Sie können Ihre Erinnerungen chronologisch organisieren, Fotos hinzufügen und mit Freunden teilen. Die Timeline ist in Lebensphasen unterteilt, um Ihnen bei der Organisation zu helfen."
        },
        {
          question: "Ist uplivion kostenlos?",
          answer: "Ja, während der Beta-Phase ist uplivion komplett kostenlos. Sie können alle Grundfunktionen nutzen, Erinnerungen hinzufügen und mit Freunden teilen. In Zukunft werden wir Premium-Features für erweiterte Funktionen anbieten."
        },
        {
          question: "Wie erstelle ich meine erste Timeline?",
          answer: "Nach der Registrierung wird automatisch eine Timeline für Sie erstellt. Diese ist bereits in Lebensphasen unterteilt. Klicken Sie auf eine Phase (z.B. 'Kindheit'), um sie zu erweitern und Ihre ersten Erinnerungen hinzuzufügen."
        }
      ]
    },
    {
      category: "Erinnerungen & Inhalte",
      questions: [
        {
          question: "Welche Arten von Inhalten kann ich hinzufügen?",
          answer: "Sie können Texte, Fotos und Details zu Ereignissen hinzufügen. Jede Erinnerung kann einen Titel, eine Beschreibung, ein Datum und mehrere Fotos enthalten. Sie können auch Tags hinzufügen, um Ihre Erinnerungen zu kategorisieren."
        },
        {
          question: "Wie viele Fotos kann ich hochladen?",
          answer: "Pro Erinnerung können Sie mehrere Fotos hochladen. Jedes Foto darf maximal 10MB groß sein. Unterstützte Formate sind JPG, PNG und WEBP. In der Beta-Phase gibt es großzügige Speicherlimits."
        },
        {
          question: "Kann ich Erinnerungen nachträglich bearbeiten oder löschen?",
          answer: "Ja, Sie können alle Ihre Erinnerungen jederzeit bearbeiten oder löschen. Klicken Sie einfach auf eine Erinnerung, um sie zu öffnen, und verwenden Sie die Bearbeiten- oder Löschen-Optionen."
        }
      ]
    },
    {
      category: "Freunde & Teilen",
      questions: [
        {
          question: "Wie finde und füge ich Freunde hinzu?",
          answer: "Gehen Sie zum Freunde-Bereich und suchen Sie nach Benutzernamen oder E-Mail-Adressen. Sie können Freundschaftsanfragen senden und erhalten. Alternativ können Freunde auch nach Ihnen suchen."
        },
        {
          question: "Wer kann meine Erinnerungen sehen?",
          answer: "Das entscheiden Sie! Jede Erinnerung hat Privatsphäre-Einstellungen: 'Nur ich' (privat), 'Freunde' (alle Ihre Freunde) oder in Zukunft auch 'Öffentlich'. Sie haben volle Kontrolle über die Sichtbarkeit."
        },
        {
          question: "Können Freunde Erinnerungen zu meiner Timeline hinzufügen?",
          answer: "Ja, Freunde können Erinnerungen zu Ihrer Timeline hinzufügen, die Sie dann genehmigen müssen. Dies ist perfekt für gemeinsame Erlebnisse oder wenn Freunde Fotos von Ihnen haben."
        }
      ]
    },
    {
      category: "Datenschutz & Sicherheit",
      questions: [
        {
          question: "Sind meine Daten sicher?",
          answer: "Ja, Datenschutz hat oberste Priorität. Alle Daten werden verschlüsselt übertragen und gespeichert. Wir folgen der DSGVO und speichern Daten nur auf Servern in der EU. Ihre Daten gehören Ihnen."
        },
        {
          question: "Wer kann auf meine Daten zugreifen?",
          answer: "Nur Sie haben Zugriff auf Ihre privaten Daten. uplivion-Mitarbeiter können nicht auf Ihre persönlichen Inhalte zugreifen. Technische Daten werden nur für Verbesserungen und Support verwendet."
        },
        {
          question: "Kann ich meine Daten exportieren oder löschen?",
          answer: "Ja, Sie haben jederzeit das Recht, Ihre Daten zu exportieren oder Ihr Konto komplett zu löschen. Export-Funktionen sind in Entwicklung, bei Bedarf kontaktieren Sie uns für einen manuellen Export."
        }
      ]
    },
    {
      category: "Beta-Phase",
      questions: [
        {
          question: "Was bedeutet 'Beta-Phase'?",
          answer: "Die Beta-Phase bedeutet, dass uplivion noch in der Entwicklung ist. Sie können alle Kernfunktionen kostenlos nutzen, aber es können gelegentlich Updates, Änderungen oder kleine Probleme auftreten. Ihr Feedback hilft uns beim Verbessern."
        },
        {
          question: "Gehen meine Daten in der Beta-Phase verloren?",
          answer: "Nein, Ihre Daten sind sicher. Wir machen regelmäßige Backups und migrieren Daten bei Updates. In sehr seltenen Fällen kann es zu technischen Problemen kommen, aber wir arbeiten hart daran, Datenverlust zu verhindern."
        },
        {
          question: "Wie kann ich Feedback geben?",
          answer: "Wir freuen uns über Ihr Feedback! Schreiben Sie uns an feedback@uplivion.life oder nutzen Sie die Feedback-Funktion in der App. Ihre Ideen und Verbesserungsvorschläge fließen direkt in die Entwicklung ein."
        }
      ]
    },
    {
      category: "Technische Fragen",
      questions: [
        {
          question: "Welche Browser werden unterstützt?",
          answer: "uplivion funktioniert am besten mit modernen Browsern wie Chrome, Firefox, Safari und Edge. Wir empfehlen, immer die neueste Version zu verwenden für die beste Erfahrung."
        },
        {
          question: "Gibt es eine mobile App?",
          answer: "Eine native mobile App für iOS und Android ist in Planung. Aktuell können Sie uplivion über den mobilen Browser nutzen - die Website ist für mobile Geräte optimiert."
        },
        {
          question: "Warum lädt meine Timeline langsam?",
          answer: "Bei vielen Erinnerungen mit Fotos kann das Laden etwas dauern. Wir arbeiten kontinuierlich an der Performance. Überprüfen Sie auch Ihre Internetverbindung und leeren Sie gelegentlich den Browser-Cache."
        }
      ]
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Zurück zur Startseite
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Häufig gestellte Fragen (FAQ)</h1>
            <p className="text-xl text-gray-600">Antworten auf die häufigsten Fragen zu uplivion.</p>
          </div>

          <div className="mb-8">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-8">
                <h2 className="text-2xl font-semibold text-uplivion-blue mb-4 border-b pb-2">
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.questions.map((faq, questionIndex) => {
                    const globalIndex = categoryIndex * 10 + questionIndex;
                    return (
                      <div 
                        key={globalIndex} 
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(globalIndex)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          {openFAQ === globalIndex ? (
                            <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                        {openFAQ === globalIndex && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-uplivion-blue/5 to-uplivion-blue/10 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-3">Ihre Frage ist nicht dabei?</h2>
            <p className="text-gray-600 mb-4">
              Kein Problem! Unser Support-Team hilft Ihnen gerne weiter. Als Beta-Nutzer erhalten 
              Sie priority Support und direkten Kontakt zu unserem Entwicklerteam.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild>
                <Link to="/contact">Kontakt aufnehmen</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/help">Hilfe-Center besuchen</Link>
              </Button>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-yellow-800 mb-2">Beta-Support</h3>
            <p className="text-yellow-700 text-sm">
              Als Beta-Nutzer haben Sie Zugang zu unserem premium Support. Wir antworten in der Regel 
              innerhalb von 24 Stunden und nehmen Ihr Feedback ernst. Ihre Erfahrungen helfen uns dabei, 
              uplivion zu der besten Plattform für persönliche Erinnerungen zu machen. Schreiben Sie uns an 
              support@uplivion.life.
            </p>
          </div>

          <div className="pt-8 border-t">
            <Button asChild>
              <Link to="/">Zur Startseite</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
